import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { productsApi } from '@/api/products'
import { productMediaApi, campaignGalleryApi } from '@/api/productMedia'
import type {
  AdjustInventoryInput,
  CampaignMediaInput,
  CategoryInput,
  HeroSlideInput,
  ProductCampaignInput,
  ProductInput,
  ProductListFilters,
  ProductUpdateInput,
  PromoTileInput,
} from '@/types/ecommerce'

/**
 * Real ProductService-backed hooks (catalog + categories + inventory), Module 13.
 *
 * Lives in admin-dashboard's own lib/ (not shared-core/src/hooks/data) — shared-core is
 * off limits for this feature; the real `productsApi` client + ecommerce types it wraps
 * already live there and are only read here, not modified. NOT to be confused with the
 * unrelated legacy `useProducts`/`useCategories` in lib/gateway.ts, which hit a different,
 * old `/api/products` CommerceService stub — this hooks into the real `/products` and
 * `/categories` ProductService routes via the typed shared-core client.
 */
const productKeys = {
  list: (filters: ProductListFilters) => ['product-catalog', 'products', filters] as const,
  detail: (id: string) => ['product-catalog', 'products', 'detail', id] as const,
  categories: (parentId?: string) => ['product-catalog', 'categories', parentId ?? 'all', 'admin'] as const,
  inventoryHistory: (id: string, cursor?: string) => ['product-catalog', 'products', id, 'inventory-history', cursor ?? 'first'] as const,
}

export function useProductListAdmin(filters: ProductListFilters) {
  return useQuery({
    queryKey: productKeys.list(filters),
    queryFn: () => productsApi.list(filters),
    placeholderData: keepPreviousData,
  })
}

export function useProductDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: productKeys.detail(id ?? ''),
    queryFn: () => productsApi.get(id as string),
    enabled: !!id,
  })
}

function invalidateProducts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'products'] })
}

export function useCreateProductAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProductInput) => productsApi.create(body),
    onSuccess: () => invalidateProducts(qc),
  })
}

export function useUpdateProductAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProductUpdateInput }) => productsApi.update(id, body),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.id) })
    },
  })
}

/** Row-level Published/Draft switch (PATCH /products/{id}/status) — the product list shows the change immediately. */
export function useSetProductStatusAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'Draft' | 'Published' | 'Archived' }) => productsApi.setStatus(id, status),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.id) })
    },
  })
}

export function useDeleteProductAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => invalidateProducts(qc),
  })
}

export function useAdjustProductInventory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdjustInventoryInput }) => productsApi.adjustInventory(id, body),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.id) })
      qc.invalidateQueries({ queryKey: ['product-catalog', 'products', variables.id, 'inventory-history'] })
    },
  })
}

export function useInventoryHistory(id: string | null | undefined, cursor?: string) {
  return useQuery({
    queryKey: productKeys.inventoryHistory(id ?? '', cursor),
    queryFn: () => productsApi.getInventoryHistory(id as string, cursor),
    enabled: !!id,
  })
}

// ── Product image/video upload (ProductMediaController — native to ProductService) ──

export function useUploadProductImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, file }: { productId: string; file: File }) =>
      productMediaApi.uploadProductImage(productId, file),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.productId) })
    },
  })
}

export function useUploadProductVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, file }: { productId: string; file: File }) =>
      productMediaApi.uploadProductVideo(productId, file),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.productId) })
    },
  })
}

export function useRemoveProductImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, imageId }: { productId: string; imageId: string }) =>
      productMediaApi.removeProductImage(productId, imageId),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.productId) })
    },
  })
}

export function useRemoveProductVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, videoId }: { productId: string; videoId: string }) =>
      productMediaApi.removeProductVideo(productId, videoId),
    onSuccess: (_data, variables) => {
      invalidateProducts(qc)
      qc.invalidateQueries({ queryKey: productKeys.detail(variables.productId) })
    },
  })
}

/** Admin management page — includeInactive so a disabled category can still be found and
 * re-enabled. The public storefront calls productsApi.listCategories directly instead (its
 * own hook, active-only default), not this one. */
export function useProductCategories(parentId?: string) {
  return useQuery({
    queryKey: productKeys.categories(parentId),
    queryFn: () => productsApi.listCategories(parentId, true),
  })
}

function invalidateCategories(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'categories'] })
}

export function useCreateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryInput) => productsApi.createCategory(body),
    onSuccess: () => invalidateCategories(qc),
  })
}

export function useUpdateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CategoryInput }) => productsApi.updateCategory(id, body),
    onSuccess: () => invalidateCategories(qc),
  })
}

export function useDeleteProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.removeCategory(id),
    onSuccess: () => invalidateCategories(qc),
  })
}

export function useToggleProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.toggleCategory(id),
    onSuccess: () => invalidateCategories(qc),
  })
}

export function useReorderProductCategories() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedIds: string[]) => productsApi.reorderCategories(orderedIds),
    onSuccess: () => invalidateCategories(qc),
  })
}

// ── Hero slides (homepage carousel) ──

export function useHeroSlidesAdmin() {
  return useQuery({
    queryKey: ['product-catalog', 'hero-slides'],
    queryFn: () => productsApi.listHeroSlides(false), // false: admin manages inactive slides too
  })
}

function invalidateHeroSlides(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'hero-slides'] })
}

export function useCreateHeroSlide() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: HeroSlideInput) => productsApi.createHeroSlide(body),
    onSuccess: () => invalidateHeroSlides(qc),
  })
}

export function useUpdateHeroSlide() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: HeroSlideInput }) => productsApi.updateHeroSlide(id, body),
    onSuccess: () => invalidateHeroSlides(qc),
  })
}

export function useDeleteHeroSlide() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.removeHeroSlide(id),
    onSuccess: () => invalidateHeroSlides(qc),
  })
}

// ── Promo tiles (homepage "more ways to shop" grid) ──

export function usePromoTilesAdmin() {
  return useQuery({
    queryKey: ['product-catalog', 'promo-tiles'],
    queryFn: () => productsApi.listPromoTiles(false), // false: admin manages inactive tiles too
  })
}

function invalidatePromoTiles(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'promo-tiles'] })
}

export function useCreatePromoTile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PromoTileInput) => productsApi.createPromoTile(body),
    onSuccess: () => invalidatePromoTiles(qc),
  })
}

export function useUpdatePromoTile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PromoTileInput }) => productsApi.updatePromoTile(id, body),
    onSuccess: () => invalidatePromoTiles(qc),
  })
}

export function useDeletePromoTile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.removePromoTile(id),
    onSuccess: () => invalidatePromoTiles(qc),
  })
}

// ── Product campaigns (homepage "special offers" carousel) ──

export function useProductCampaignsAdmin() {
  return useQuery({
    queryKey: ['product-catalog', 'product-campaigns'],
    queryFn: () => productsApi.listCampaigns(false), // false: admin manages scheduled/expired/paused too
  })
}

function invalidateCampaigns(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'product-campaigns'] })
}

export function useCreateProductCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProductCampaignInput) => productsApi.createCampaign(body),
    onSuccess: () => invalidateCampaigns(qc),
  })
}

export function useUpdateProductCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProductCampaignInput }) => productsApi.updateCampaign(id, body),
    onSuccess: () => invalidateCampaigns(qc),
  })
}

export function useDeleteProductCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => productsApi.removeCampaign(id),
    onSuccess: () => invalidateCampaigns(qc),
  })
}

// ── Campaign media gallery (CampaignMedia child rows) ──

export function useCampaignMedia(campaignId: string | null) {
  return useQuery({
    queryKey: ['product-catalog', 'campaign-media', campaignId],
    queryFn: () => campaignGalleryApi.list(campaignId as string),
    enabled: !!campaignId,
  })
}

function invalidateCampaignMedia(qc: ReturnType<typeof useQueryClient>, campaignId: string) {
  qc.invalidateQueries({ queryKey: ['product-catalog', 'campaign-media', campaignId] })
}

export function useUploadCampaignMediaImage(campaignId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => campaignGalleryApi.uploadImage(campaignId, file),
    onSuccess: () => invalidateCampaignMedia(qc, campaignId),
  })
}

export function useUploadCampaignMediaVideo(campaignId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => campaignGalleryApi.uploadVideo(campaignId, file),
    onSuccess: () => invalidateCampaignMedia(qc, campaignId),
  })
}

export function useAddCampaignMediaEmbed(campaignId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CampaignMediaInput) => campaignGalleryApi.addEmbed(campaignId, body),
    onSuccess: () => invalidateCampaignMedia(qc, campaignId),
  })
}

export function useDeleteCampaignMedia(campaignId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mediaId: string) => campaignGalleryApi.remove(campaignId, mediaId),
    onSuccess: () => invalidateCampaignMedia(qc, campaignId),
  })
}

export function useReorderCampaignMedia(campaignId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedMediaIds: string[]) => campaignGalleryApi.reorder(campaignId, orderedMediaIds),
    onSuccess: () => invalidateCampaignMedia(qc, campaignId),
  })
}
